"use client";

import * as R from "ramda";
import { useQuery } from "@apollo/client/react";

import { useEnvironmentVariables } from "@/contexts/environmentVariables";
import { isE2EBuild, isMockMode } from "@/lib/runtimeMode";
import { queries } from "@/api";

import {
	type PossibleFeatureFlags,
	featureFlags,
	environmentVariableToBoolean,
} from "./feature-flags";

export type FlagRecord = Record<PossibleFeatureFlags, boolean>;

/**
 * Under the Playwright suite every client flag comes from the `features` query
 * the spec mocks, so the env-based branches below stand aside.
 */
const isE2E = isE2EBuild();

// Holds only the flags whose environment variable is set.
export const environmentFeatures: Partial<FlagRecord> = Object.fromEntries(
	Object.entries(featureFlags).flatMap(([key, flag]) =>
		R.isNil(flag.env)
			? []
			: [
					[
						key,
						environmentVariableToBoolean(
							flag.env as "1" | "0" | "false" | "true",
						),
					] as const,
				],
	),
);

export const useFeatureFlagsQuery = () => useQuery(queries.FEATURES);

/**
 * True while `useFeatureFlag(flag)` could still change its answer, because the
 * GraphQL `features` query is the source for this flag and has not answered.
 *
 * A caller that branches on a flag before the flag is known — a state machine
 * deciding whether to show a step, say — must hold until this is false. The
 * branches below mirror `useFeatureFlag`: local-dev, mock mode and the
 * environment flags all answer without the query, so they never report loading.
 */
export const useIsFeatureFlagLoading = (flag: PossibleFeatureFlags) => {
	const { runtimeEnvironment } = useEnvironmentVariables();
	const { loading } = useFeatureFlagsQuery();

	if (runtimeEnvironment === "local" && !isE2E) {
		return false;
	}

	if (isMockMode() && !isE2E) {
		return false;
	}

	if (flag in environmentFeatures && !isE2E) {
		return false;
	}

	return loading;
};

const useFeatureFlag = (flag: PossibleFeatureFlags) => {
	const { runtimeEnvironment } = useEnvironmentVariables();

	const { data = {} } = useFeatureFlagsQuery();
	const { features = [] } = data;
	const queryFeatures: Record<string, boolean> = Object.fromEntries(
		features.map(
			({ name, isEnabled }: { name: string; isEnabled: boolean }) => [
				name,
				isEnabled,
			],
		),
	);

	if (!flag) {
		throw new Error(
			"useFeatureFlag checks against a single feature. No feature flag was supplied.",
		);
	}

	// In local-dev, mirror the server (lookUpFeature): resolve flags from the
	// synced local-dev feature-flags only and skip the GraphQL `features` query,
	// so server and client components never disagree on the same flag. The
	// Playwright suite keeps its own override path.
	if (runtimeEnvironment === "local" && !isE2E) {
		return environmentFeatures[flag] ?? false;
	}

	// Parallel to the local-dev branch above, for the same reason and by a
	// different route. The server reads mock-dev/feature-flags.env directly
	// (mockFlags.ts); `pnpm mock` exports the same file as
	// NEXT_PUBLIC_FEATURE_FLAG_* so it lands in environmentFeatures here. Without
	// this branch a flag absent from the file would fall through to the GraphQL
	// `features` query, which the mock engine answers with an empty list — the
	// same answer, but reached over the network and one render later, so server
	// and client would disagree for that render.
	if (isMockMode() && !isE2E) {
		return environmentFeatures[flag] ?? false;
	}

	if (flag in environmentFeatures && !isE2E) {
		return environmentFeatures[flag] ?? false;
	}

	if (flag in queryFeatures) {
		return queryFeatures[flag];
	}

	return false;
};

export default useFeatureFlag;
